import { Icon, IDropdownOption, mergeStyleSets, Stack, Text, useTheme } from '@fluentui/react';

import React from 'react';
import {
  AppContext,
  MenuFilters
} from '../../AppInRouterContext';
import { genkey, hash } from '../../common/constants';
import { Club, ClubContext, TorneoEnDivision, TorneosXDivisionXFederacion } from '../../system/SystemClient';

import { useNavigate } from 'react-router-dom';
import { onRenderOptionCategoria } from '../Common/RenderOptions';

import VerticalFilterStack from './VerticalFilterStack';

interface IResumenTorneoProps {
  menuFilters: MenuFilters;
  appContext: AppContext;
  onChangeFilters: (criterio: string, value: string) => void;

  categorias: IDropdownOption<any>[];
  temporadas: IDropdownOption<any>[];
  federaciones: IDropdownOption<any>[];
  ramas: IDropdownOption<any>[];
  clubes: Club[];
  clubId: string;
  setClubId: (v: string) => void;
  clubContext: ClubContext | undefined;
}

const useDivisionStyles = () => {
  const theme = useTheme();
  return mergeStyleSets({
    card: {
      border: `1px solid ${theme.palette.neutralLight}`,
      borderRadius: 2,
      background: theme.palette.white,
      padding: 16,
      marginBottom: 16,
      boxShadow: '0 1px 3px rgba(0,0,0,.03), 0 8px 20px rgba(0,0,0,.03)'
    },
    header: {
      marginBottom: 4,
    },
    title: {
      fontWeight: 500,
    },
    badge: {
      background: theme.palette.themePrimary,
      color: theme.palette.white,
      borderRadius: 9999,
      fontSize: 12,
      lineHeight: '20px',
      height: 20,
      minWidth: 24,
      padding: '0 8px',
      textAlign: 'center',
      display: 'none'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
      gap: 10,
      marginTop: 8,
    },
  });
};

const ResumenTorneos = (props: IResumenTorneoProps) => {
  const [torneosPorDivision, setTorneosPorDivision] = React.useState<TorneosXDivisionXFederacion[]>([]);
  const [showDropdownTemporada, setShowDropdownTemporada] = React.useState<boolean>(true);
  const [showDropdownClubes, setShowDropdownClubes] = React.useState<boolean>(true);

  React.useEffect(() => {
    const getTorneosPorDivision = async () => {
      if (
        props.menuFilters.federacionId &&
        props.menuFilters.temporadaId &&
        props.menuFilters.categoriaId &&
        props.menuFilters.rama
      ) {
        try {
          props.appContext.changeBlockUI(true);
          const ret = await props.appContext.system.getTorneosXDivision(
            props.menuFilters.federacionId,
            props.menuFilters.temporadaId,
            props.menuFilters.rama,
            props.menuFilters.categoriaId
          );
          setTorneosPorDivision(ret);
        } catch (error) {
          console.log(error);
        } finally {
          props.appContext.changeBlockUI(false);
        }
      }
    };
    getTorneosPorDivision();
  }, [props.menuFilters]);

  const navigate = useNavigate();

  const onChange = (criterio: string, value: string): void => {
    props.onChangeFilters(criterio, value);
  };
  const [step, setStep] = React.useState<number>(
    props.appContext.system.getEnvironment().federacionDefault() ? 1 : 0
  );

  const getIconByEstado = (estado: string) => {
    switch (estado) {
      case 'Finalizado':
        return 'Ribbon';
      case 'En juego':
        return 'WorldClock';
      case 'Nuevo':
        return 'Megaphone';
      default:
        return 'Trophy2';
    }
  };

  const getColorIconByEstado = (estado: string) => {
    switch (estado) {
      case 'Finalizado':
        return '#059104';
      case 'En juego':
        return '#ff9800';
      case 'Nuevo':
        return '#2788d5';
      default:
        return '#2788d5';
    }
  };


  const clubHasRama = (id: string) => {
    if (props.clubId && props.clubContext) {
      return props.clubContext.temporadas
        .find((t) => t.id == props.menuFilters.temporadaId)
        ?.ramas.some((rama) => rama.id === id);
    } else {
      return true;
    }
  };

  const clubHasCategoria = (id: string) => {
    if (props.clubId && props.clubContext) {
      return props.clubContext.temporadas
        .find((t) => t.id == props.menuFilters.temporadaId)
        ?.ramas.find((rama) => rama.id === props.menuFilters.rama)
        ?.categorias.some((categoria) => categoria.id === id);
    } else {
      return true;
    }
  };

  const clubHasCategoriaName = (id: string, text: string) => {
    if (props.clubId && props.clubContext) {
      const categoria = props.clubContext.temporadas
        .find((t) => t.id == props.menuFilters.temporadaId)
        ?.ramas.find((rama) => rama.id === props.menuFilters.rama)
        ?.categorias.find((categoria) => categoria.id === id);

      if (!categoria) {
        return text.split('(')[0] + ' (Sin torneos)';
      }
      const cantTorneos =
        categoria?.divisiones.reduce((acc, division) => acc + division.torneos.length, 0) ?? 0;

      return `${categoria?.nombre} (${cantTorneos})`;
    } else {
      return text;
    }
  };

  const clubHasDivision = (id: string) => {
    if (props.clubId && props.clubContext) {
      return props.clubContext.temporadas
        .find((t) => t.id == props.menuFilters.temporadaId)
        ?.ramas.find((rama) => rama.id === props.menuFilters.rama)
        ?.categorias.find((categoria) => categoria.id === props.menuFilters.categoriaId)
        ?.divisiones.some((division) => division.id === id);
    } else {
      return true;
    }
  };

  const clubHasTorneo = (divId: string, torId: string) => {
    if (props.clubId && props.clubContext) {
      return props.clubContext.temporadas
        .find((t) => t.id == props.menuFilters.temporadaId)
        ?.ramas.find((rama) => rama.id === props.menuFilters.rama)
        ?.categorias.find((categoria) => categoria.id === props.menuFilters.categoriaId)
        ?.divisiones.find((division) => division.id === divId)
        ?.torneos.some((torneo) => torneo.id === torId);
    } else {
      return true;
    }
  };

  const clubSelected = React.useMemo(() => {
    if (props.clubId && props.clubes) {
      return props.clubes.find((c) => c.clubId === props.clubId);
    }
  }, [props.clubes, props.clubId]);

  const stylesFemBtn = {
    root: {
      borderColor: '#bc0074ff',
      backgroundColor: props.menuFilters.rama === 'F' ? '#e3008c' : 'white',
      color: props.menuFilters.rama === 'F' ? 'white' : '#e3008c',
      border: `1px solid #0c1c4c`,
    },
    rootHovered: {
      borderColor: '#bc0074ff',
      backgroundColor: props.menuFilters.rama === 'F' ? '#e3008c' : '#f3f2f1',
      color: props.menuFilters.rama === 'F' ? 'white' : '#e3008c',
    },
    rootPressed: {
      borderColor: '#bc0074ff',
      backgroundColor: props.menuFilters.rama === 'F' ? '#e3008c' : '#e1dfdd',
      color: props.menuFilters.rama === 'F' ? 'white' : '#e3008c',
    },
  };

  const classes = useDivisionStyles();

  const BAND_PCT = 28

  return (
    < >
      <div className="ms-Grid mt-2" dir="ltr" style={{ marginBottom: 10 }}>
        <div className="ms-Grid-col ms-sm12">
          <div className="ms-Grid-row ">
            <div className="ms-Grid bg-white-h100 ms-depth-4" dir="ltr">
              <>
                <div className="ms-Grid-row padding-1">
                  <div className="ms-Grid-col ms-md8 ms-sm12">
                    <Text className="nombre-header-torneo">Resumen de torneos</Text>
                    <Text variant="smallPlus" className="mt-1" style={{ display: 'block' }}>
                      Fixture en vivo, tabla de posiciones y los goleadores de todos los torneos.
                    </Text>
                  </div>
                </div>
              </>
            </div>
          </div>
        </div>
      </div>


      <div className="ms-Grid-row mb-2">
        <div className="ms-Grid-col ms-sm12">
          <div className={`ms-Grid-col ms-sm12 ms-motion-scaleDownIn`}>
            <VerticalFilterStack
              // visibilidad de pasos (igual que antes)
              showDropdownClubes={showDropdownClubes}
              showDropdownTemporada={showDropdownTemporada}
              federacionDefault={props.appContext.system.getEnvironment().federacionDefault()}

              // estado/handlers existentes
              clubId={props.clubId}
              setClubId={props.setClubId}
              menuFilters={props.menuFilters}
              onChange={onChange}                    // tu onChange(CRITERIO_*, val)
              onChangeFilters={props.onChangeFilters}

              // datos
              clubes={props.clubes}
              temporadas={props.temporadas}
              federaciones={props.federaciones}
              ramas={props.ramas}
              categorias={props.categorias}

              // reglas y renderers que ya usás
              clubHasRama={clubHasRama}
              clubHasCategoria={clubHasCategoria}
              clubHasCategoriaName={clubHasCategoriaName}
              onRenderOptionCategoria={onRenderOptionCategoria}
              clubContext={props.clubContext}
              activeDefault='rama'
            />
          </div>
        </div>
      </div>


      {!props.menuFilters.federacionId ||
        !props.menuFilters.temporadaId ||
        !props.menuFilters.categoriaId ||
        !props.menuFilters.rama ? (
        <div className="ms-Grid mt-2" dir="ltr" style={{ marginBottom: 10 }}>
          <div className="ms-Grid-col ms-sm12  ">
            <div className="ms-Grid-row ">
              <div className="ms-Grid bg-white-h100 ms-depth-4" dir="ltr">
                <>
                  <div className="ms-Grid-row padding-1">
                    <div className="ms-Grid-col ms-sm12 text-center">
                      <Text variant="medium">
                        Podes buscar los torneos aplicando los filtros en el menu de arriba
                      </Text>
                    </div>
                  </div>
                </>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="ms-Grid mt-2" dir="ltr" style={{ marginBottom: 10, paddingLeft: 0, paddingRight: 0 }}>
          <div className="ms-Grid-col ms-sm12 mb-5">
            <div className="ms-Grid bg-white-h100 ms-depth-4 ms-motion-scaleDownIn" dir="ltr">
              <div className="ms-Grid-row padding-1">
                <div className="ms-Grid-col ms-sm12">
                  <Text variant="xLarge">Torneos</Text>
                  <div style={{ padding: '10px' }}></div>
                  {torneosPorDivision.map((txd) => {
                    return (
                      <div className="ms-Grid-row" key={genkey()}>
                        <div className="ms-Grid-col ms-sm12" style={{padding: 0}}>
                          {txd.divisiones
                            .filter(
                              (d) =>
                                clubHasDivision(d.id) &&
                                d.torneos.filter(
                                  (torneo: TorneoEnDivision) =>
                                    torneo.rama === props.menuFilters.rama &&
                                    torneo.categoriaId === props.menuFilters.categoriaId
                                ).length > 0
                            )
                            .map((d) => {
                              const filteredTorneos = d.torneos.filter(
                                (torneo: TorneoEnDivision) =>
                                  clubHasTorneo(d.id, torneo.id) &&
                                  torneo.rama === props.menuFilters.rama &&
                                  torneo.categoriaId === props.menuFilters.categoriaId
                              );
                              if (filteredTorneos.length === 0) return null;

                              return (
                                <Stack className={classes.card} tokens={{ childrenGap: 8 }} key={genkey()}>
                                  {/* Header de la división */}
                                  <Stack horizontal horizontalAlign="space-between" verticalAlign="center" className={classes.header}>
                                    <Text variant="medium" className={classes.title} style={{ marginBottom: '5px'}}>
                                      {d.competencia} - {d.nombre}
                                    </Text>
                                    <span className={classes.badge}>{filteredTorneos.length}</span>
                                  </Stack>

                                  {/* Grid de tarjetas */}
                                  <div className={classes.grid}>
                                    {filteredTorneos.map((torneo: TorneoEnDivision) => (
                                      <div
                                        key={torneo.id}
                                        className="bg-white-h100 ms-motion-scaleDownIn tournament-box clickable"
                                        onClick={() => {
                                          let password = props.appContext.system.getEnvironment().passphrase();
                                          const idHashed = hash(torneo.id, password);
                                          navigate(`/${idHashed}${!!props.clubId ? `?clubId=${props.clubId}` : ''}`);
                                        }}
                                        style={{
                                          position: 'relative',
                                          display: 'flex',
                                          alignItems: 'stretch',
                                          borderRadius: 2,           // tu ajuste
                                          overflow: 'hidden',
                                          border: '1px solid #e5e5e5',
                                          cursor: 'pointer',
                                          minHeight: 80,
                                          padding: 0,                // elimina padding heredado de clases
                                          boxSizing: 'border-box',
                                          background: '#fff',
                                        }}
                                      >
                                        {/* Banda de color sin gap a la izquierda */}
                                        <div
                                          aria-hidden
                                          style={{
                                            position: 'absolute',
                                            left: 0,
                                            top: 0,
                                            bottom: 0,
                                            width: `${BAND_PCT}%`,                 // EXACTO al tamaño visual actual
                                            background: getColorIconByEstado(torneo.estado),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                          }}
                                        >
                                          <Icon
                                            iconName={getIconByEstado(torneo.estado)}
                                            styles={{ root: { color: '#fff', fontSize: 20 } }}
                                            title={torneo.estado}
                                          />
                                        </div>

                                        {/* Contenido, desplazado a la derecha de la banda */}
                                        <div
                                          style={{
                                            flex: '1 1 auto',
                                            minWidth: 0,
                                            padding: '8px 12px',
                                            paddingLeft: `calc(${BAND_PCT}% + 12px)`, // compensa la banda + espacio de respiro
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            gap: 2,
                                          }}
                                        >
                                          <Text
                                            variant="smallPlus"
                                            styles={{
                                              root: {
                                                fontWeight: 600,
                                                color: '#0c1c4c',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                lineHeight: '18px',
                                              },
                                            }}
                                            title={torneo.nombre}
                                          >
                                            {torneo.nombre}
                                          </Text>

                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </Stack>
                              );
                            })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ResumenTorneos;
